import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Ceil } from "@pdfking/math"
import type { ColorID, PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign } from "@pdfking/renderer-api"
import type { Atom } from "@pdfking/tfunc"
import { RecordMerge } from "@pdfking/tfunc"

import { MONTH_LAYOUT, PALETTE, WEEK_ROWS } from "#constants"
import type { CalendarArea, ContentLabels, MonthLayout } from "#internal"

import { Slice } from "../layout/Slice"
import { CaptionText } from "./CaptionText"
import { MonthName } from "./MonthName"

type Box = SpatialRectangle<Distance>

/** The areas a calendar's design fills its pages with, each in `box`, its
 * other options as most designs have them (see CalendarArea). */
export const Area = {
  badge: (box: Box, lines: string[]): CalendarArea => ({ box, enum: "badge", lines, spot: false }),
  /** A line of text on a picture: in the secondary, on a band of paper the
   * box wide, so it reads whatever is under it. */
  caption: (
    box: Box,
    text: string,
    align: HorizontalAlign = HorizontalAlign.CENTER,
    vertical = false
  ): CalendarArea[] => [
    { box, color: PALETTE.paper, enum: "solid" },
    { align, box, color: PALETTE.secondary, enum: "text", is_year: false, text, vertical },
  ],
  /** The calendar's footer across `box` (see CalendarArea) */
  footer: (box: Box): CalendarArea => ({ box, enum: "footer" }),
  contact: (box: Box, right = true, solid = false): CalendarArea => ({
    box,
    enum: "contact",
    right,
    solid,
  }),
  /** Month `month`'s days down `columns` lists across `box`, `gap` apart,
   * as many in each */
  columns: (
    box: Box,
    month: number,
    year: number,
    columns: number,
    gap: Distance
  ): CalendarArea[] => {
    const rows = Ceil(31 / columns)
    return Slice.grid(box, columns, 1, gap).map((place, i) =>
      Area.days(place, month, year, 1 + rows * i, rows * (i + 1))
    )
  },
  days: (box: Box, month: number, year: number, first = 1, last = 31): CalendarArea => ({
    aligned: false,
    box,
    enum: "days",
    first,
    last,
    month,
    rows: last - first + 1,
    year,
  }),
  /** A month's days down `box` on the rows of their weekdays, as a year's
   * columns line them up: the Monday of the week of the 1st on the first. */
  weeks: (box: Box, month: number, year: number): CalendarArea => ({
    aligned: true,
    box,
    enum: "days",
    first: 1,
    last: 31,
    month,
    rows: WEEK_ROWS,
    year,
  }),
  /** Month `month`'s grid, set as `layout` changes the large calendars'
   * (see MONTH_LAYOUT). */
  grid: (
    box: Box,
    month: number,
    year: number,
    layout: Partial<MonthLayout> = {}
  ): CalendarArea => ({
    box,
    enum: "grid",
    layout: RecordMerge(MONTH_LAYOUT, layout),
    month,
    year,
  }),
  logo: (box: Box, labels: ContentLabels): CalendarArea => ({
    box,
    enum: "badge",
    lines: [labels.LOGO_TOP(), labels.LOGO_BOTTOM()],
    spot: true,
  }),
  /** Month `month`'s name in `language` `title` tall over its grid, `gap`
   * under it */
  month: (
    box: Box,
    month: number,
    year: number,
    language: string,
    title: Distance,
    gap: Distance
  ): CalendarArea[] => {
    const [head, rest] = Slice.top(box, title, gap)
    return [Area.title(head, month, language), Area.grid(rest, month, year)]
  },
  /** The year's months `first` to `last`, small, `columns` across `box`,
   * each its name and year over it, set as `layout` changes that. */
  months: (
    box: Box,
    year: number,
    columns: number,
    gap: Distance,
    first = 1,
    last = 12,
    layout: Partial<MonthLayout> = {}
  ): CalendarArea[] =>
    Slice.grid(box, columns, Ceil((last - first + 1) / columns), gap, last - first + 1).map(
      (place, i) => Area.grid(place, first + i, year, { title: "month_year", ...layout })
    ),
  /** A picture, its `over` the year when `is_year` */
  picture: (
    box: Box,
    salt: Atom = "",
    round = false,
    over: PageShape[] = [],
    is_year = false
  ): CalendarArea => ({
    box,
    enum: "picture",
    is_year,
    over,
    round,
    salt,
  }),
  /** A picture with `text` on it in the secondary, as tall as `caption` and
   * set in it as `align` has it, or up it when `vertical`, the picture clear
   * round it */
  titled: (
    box: Box,
    salt: Atom,
    caption: Box,
    text: string,
    align: HorizontalAlign = HorizontalAlign.CENTER,
    vertical = false
  ): CalendarArea => Area.picture(box, salt, false, CaptionText(caption, text, align, vertical)),
  /** A perforation's dashes down the middle of `box` */
  perforation: (box: Box): CalendarArea => ({ box, enum: "perforation" }),
  /** Lines across `box` `pitch` apart, for notes */
  rules: (box: Box, pitch: Distance): CalendarArea => ({ box, enum: "rules", pitch }),
  solid: (box: Box, color: ColorID = PALETTE.primary): CalendarArea => ({
    box,
    color,
    enum: "solid",
  }),
  strip: (box: Box, month: number, year: number, ruled = true): CalendarArea => ({
    box,
    enum: "strip",
    month,
    ruled,
    year,
  }),
  text: (
    box: Box,
    text: string,
    color: ColorID = PALETTE.secondary,
    align: HorizontalAlign = HorizontalAlign.LEFT,
    vertical = false
  ): CalendarArea => ({ align, box, color, enum: "text", is_year: false, text, vertical }),
  /** `year` in the secondary from `box`'s left, the year's role (see
   * CalendarArea) */
  year: (box: Box, year: number): CalendarArea => ({
    align: HorizontalAlign.LEFT,
    box,
    color: PALETTE.secondary,
    enum: "text",
    is_year: true,
    text: `${year}`,
    vertical: false,
  }),
  /** Month `month`'s name in `language`, as its page has it over its days. */
  title: (
    box: Box,
    month: number,
    language: string,
    color: ColorID = PALETTE.secondary
  ): CalendarArea => ({
    align: HorizontalAlign.LEFT,
    box,
    color,
    enum: "text",
    is_year: false,
    text: MonthName(month, language).toUpperCase(),
    vertical: false,
  }),
}
